import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Link as LinkIcon, Copy, Plus, Video, Phone, MapPin, Clock, Calendar as CalIcon, Trash2, Edit, ExternalLink, Users2, Mail, FileText, CheckCircle2, UserX } from 'lucide-react';
import { toast } from 'sonner';

const DAY_ORDER = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'],
  ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday'],
];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Kolkata',
  'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney', 'UTC',
];

const LOCATION_ICONS = { video: Video, phone: Phone, in_person: MapPin };

const EMPTY_MT = { name: '', description: '', duration_minutes: 30, location_type: 'video', location_details: '', color: '#E4007C', is_active: true };

export default function BookingSettingsPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editMT, setEditMT] = useState(null);
  const [mtForm, setMtForm] = useState(EMPTY_MT);
  const [bookings, setBookings] = useState([]);

  const setBookingStatus = async (bid, status) => {
    try {
      await api.patch(`/booking/bookings/${bid}/status`, { status });
      toast.success(status === 'confirmed' ? 'Status reset' : `Marked as ${status.replace('_', '-')}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update status'); }
  };

  const load = async () => {
    const [p, t, b] = await Promise.all([
      api.get('/booking/profile'),
      api.get('/booking/meeting-types'),
      api.get('/booking/bookings').catch(() => ({ data: [] })),
    ]);
    setProfile(p.data);
    setTypes(t.data);
    setBookings(b.data || []);
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put('/booking/profile', { ...profile, ...patch });
      setProfile(r.data);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const publicUrl = profile ? `${window.location.origin}/book/${profile.slug}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('Booking link copied');
  };

  const setDayWindow = (day, idx, field, value) => {
    const wh = { ...(profile.working_hours || {}) };
    const arr = [...(wh[day] || [])];
    arr[idx] = [...(arr[idx] || ['09:00', '17:00'])];
    arr[idx][field === 'start' ? 0 : 1] = value;
    wh[day] = arr;
    setProfile({ ...profile, working_hours: wh });
  };

  const addDayWindow = (day) => {
    const wh = { ...(profile.working_hours || {}) };
    wh[day] = [...(wh[day] || []), ['09:00', '17:00']];
    setProfile({ ...profile, working_hours: wh });
  };

  const removeDayWindow = (day, idx) => {
    const wh = { ...(profile.working_hours || {}) };
    wh[day] = (wh[day] || []).filter((_, i) => i !== idx);
    setProfile({ ...profile, working_hours: wh });
  };

  const saveMT = async () => {
    if (!mtForm.name) return toast.error('Name required');
    try {
      if (editMT?.id) {
        await api.put(`/booking/meeting-types/${editMT.id}`, mtForm);
      } else {
        await api.post('/booking/meeting-types', mtForm);
      }
      toast.success('Saved meeting type');
      setEditMT(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeMT = async (id) => {
    if (!confirm('Delete this meeting type? Existing bookings are not affected.')) return;
    try { await api.delete(`/booking/meeting-types/${id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [blackoutInput, setBlackoutInput] = useState('');
  const addBlackout = () => {
    if (!blackoutInput || (profile.blackout_dates || []).includes(blackoutInput)) return;
    setProfile({ ...profile, blackout_dates: [...(profile.blackout_dates || []), blackoutInput].sort() });
    setBlackoutInput('');
  };
  const removeBlackout = (d) => setProfile({ ...profile, blackout_dates: (profile.blackout_dates || []).filter((x) => x !== d) });

  if (!profile) return <div className="text-slate-500 text-sm p-6">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="booking-settings-page">
      {/* Public link */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><LinkIcon className="w-5 h-5 text-magenta" /> Your public booking link</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex-wrap">
            <code className="flex-1 min-w-[180px] text-sm text-navy font-mono truncate" data-testid="booking-url">{publicUrl}</code>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-booking-link"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
            <Button size="sm" variant="outline" onClick={() => window.open(publicUrl, '_blank')} data-testid="open-booking-link"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview</Button>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Slug (part after /book/)</Label>
              <Input value={profile.slug} onChange={(e) => setProfile({ ...profile, slug: e.target.value })} onBlur={() => save({ slug: profile.slug })} data-testid="booking-slug" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={profile.is_public} onCheckedChange={(v) => save({ is_public: v })} data-testid="booking-public-toggle" />
              <Label className="text-xs cursor-pointer">Publicly listed</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Users2 className="w-5 h-5 text-magenta" /> Public profile</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Display name</Label><Input value={profile.display_name || ''} onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} onBlur={() => save({})} data-testid="booking-display-name" /></div>
            <div><Label className="text-xs">Headline</Label><Input value={profile.headline || ''} onChange={(e) => setProfile({ ...profile, headline: e.target.value })} onBlur={() => save({})} placeholder="e.g. Partnership Manager" data-testid="booking-headline" /></div>
          </div>
          <div>
            <Label className="text-xs">Bio</Label>
            <Textarea rows={3} value={profile.bio || ''} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} onBlur={() => save({})} placeholder="A short intro shown to bookers" data-testid="booking-bio" />
          </div>
        </CardContent>
      </Card>

      {/* Meeting types */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-magenta" /> Meeting types</CardTitle>
            <Button size="sm" onClick={() => { setEditMT({}); setMtForm(EMPTY_MT); }} className="bg-magenta hover:bg-magenta/90" data-testid="add-meeting-type"><Plus className="w-4 h-4 mr-1" /> Add type</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {types.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No meeting types yet</div>}
          {types.map((t) => {
            const Icon = LOCATION_ICONS[t.location_type] || Video;
            return (
              <div key={t.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-3 hover:bg-slate-50" data-testid={`meeting-type-${t.id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: (t.color || '#E4007C') + '15', color: t.color || '#E4007C' }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-navy flex items-center gap-2">
                    {t.name}
                    {!t.is_active && <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase">Hidden</span>}
                  </div>
                  <div className="text-xs text-slate-500">{t.duration_minutes} min · {t.location_type.replace('_', ' ')}{t.location_details ? ` · ${t.location_details}` : ''}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setEditMT(t); setMtForm({ ...t }); }} data-testid={`edit-mt-${t.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => removeMT(t.id)} className="text-rose-600" data-testid={`delete-mt-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Availability */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><CalIcon className="w-5 h-5 text-magenta" /> Availability</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Timezone</Label>
              <Select value={profile.timezone} onValueChange={(v) => save({ timezone: v })}>
                <SelectTrigger data-testid="booking-timezone"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">{TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Buffer between meetings (minutes)</Label>
              <Input type="number" min={0} max={120} value={profile.buffer_minutes} onChange={(e) => setProfile({ ...profile, buffer_minutes: Number(e.target.value) })} onBlur={() => save({})} data-testid="booking-buffer" />
            </div>
            <div>
              <Label className="text-xs">Minimum notice (hours before)</Label>
              <Input type="number" min={0} value={profile.min_notice_hours} onChange={(e) => setProfile({ ...profile, min_notice_hours: Number(e.target.value) })} onBlur={() => save({})} data-testid="booking-notice" />
            </div>
            <div>
              <Label className="text-xs">Max advance (days)</Label>
              <Input type="number" min={1} max={365} value={profile.max_advance_days} onChange={(e) => setProfile({ ...profile, max_advance_days: Number(e.target.value) })} onBlur={() => save({})} data-testid="booking-max-advance" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-heading">Working hours</Label>
            <div className="mt-2 space-y-2">
              {DAY_ORDER.map(([key, label]) => (
                <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-1.5 border-b border-slate-50 sm:border-0">
                  <div className="w-24 shrink-0 text-sm font-semibold text-navy pt-2">{label}</div>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {((profile.working_hours || {})[key] || []).length === 0 && (
                      <div className="text-xs text-slate-400 italic pt-2">Unavailable</div>
                    )}
                    {((profile.working_hours || {})[key] || []).map((w, idx) => (
                      <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`wh-${key}-${idx}`}>
                        <Input type="time" value={w[0]} onChange={(e) => setDayWindow(key, idx, 'start', e.target.value)} onBlur={() => save({})} className="w-28" />
                        <span className="text-slate-400">–</span>
                        <Input type="time" value={w[1]} onChange={(e) => setDayWindow(key, idx, 'end', e.target.value)} onBlur={() => save({})} className="w-28" />
                        <Button size="sm" variant="ghost" onClick={() => { removeDayWindow(key, idx); save({}); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { addDayWindow(key); save({}); }} data-testid={`wh-add-${key}`} className="self-start sm:self-auto"><Plus className="w-3.5 h-3.5 mr-1" /> Slot</Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-heading">Blackout dates</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input type="date" value={blackoutInput} onChange={(e) => setBlackoutInput(e.target.value)} className="max-w-[180px]" data-testid="blackout-input" />
              <Button size="sm" variant="outline" onClick={() => { addBlackout(); save({ blackout_dates: [...(profile.blackout_dates || []), blackoutInput].sort() }); }} data-testid="add-blackout"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(profile.blackout_dates || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 text-xs px-2 py-1 rounded-full">
                  {d}
                  <button onClick={() => { const rest = (profile.blackout_dates || []).filter((x) => x !== d); setProfile({ ...profile, blackout_dates: rest }); save({ blackout_dates: rest }); }} className="hover:text-rose-900">×</button>
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming bookings */}
      <Card>
        <CardHeader><CardTitle>Your bookings</CardTitle></CardHeader>
        <CardContent>
          {bookings.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No bookings yet — share your link to receive some.</div>}
          <div className="space-y-2">
            {bookings.slice(0, 20).map((b) => (
              <div key={b.id} className="flex items-center justify-between border-b border-slate-100 py-2 flex-wrap gap-2" data-testid={`booking-${b.id}`}>
                <div>
                  <div className="font-semibold text-sm text-navy flex items-center gap-2 flex-wrap">
                    {b.attendee_name} · {b.meeting_type_name}
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                      b.status === 'completed' ? 'bg-emerald-100 text-emerald-700'
                      : b.status === 'no_show' ? 'bg-rose-100 text-rose-600'
                      : b.status === 'cancelled' ? 'bg-slate-200 text-slate-500'
                      : 'bg-sky-100 text-sky-700'}`} data-testid={`booking-status-${b.id}`}>
                      {(b.status || 'confirmed').replace('_', '-')}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">{new Date(b.start_at).toLocaleString()} · {b.attendee_email}</div>
                  {b.brief_sent_at && <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">Brief sent {new Date(b.brief_sent_at).toLocaleString()}</div>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {!['completed', 'no_show', 'cancelled'].includes(b.status) && (
                    <>
                      <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => setBookingStatus(b.id, 'completed')} data-testid={`mark-completed-${b.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Completed
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => setBookingStatus(b.id, 'no_show')} data-testid={`mark-noshow-${b.id}`}>
                        <UserX className="w-3.5 h-3.5 mr-1" /> No-show
                      </Button>
                    </>
                  )}
                  {['completed', 'no_show', 'cancelled'].includes(b.status) && (
                    <Button size="sm" variant="ghost" className="text-slate-400"
                      onClick={() => setBookingStatus(b.id, 'confirmed')} data-testid={`undo-status-${b.id}`}>
                      Undo
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('aawta_token');
                        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/booking/${b.id}/brief-preview`, { headers: { Authorization: `Bearer ${token}` } });
                        const html = await r.text();
                        const w = window.open('', '_blank');
                        if (w) { w.document.write(html); w.document.close(); }
                      } catch { toast.error('Preview failed'); }
                    }}
                    data-testid={`preview-brief-${b.id}`}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" /> Preview brief
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={async () => {
                      try {
                        const r = await api.post(`/booking/${b.id}/send-brief-now`);
                        toast.success(`Brief sent to ${r.data.sent_to}`);
                        load();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }}
                    data-testid={`send-brief-${b.id}`}
                  >
                    <Mail className="w-3.5 h-3.5 mr-1" /> Send now
                  </Button>
                  <button onClick={() => navigate('/leads')} className="text-xs text-magenta font-semibold hover:underline">Open CRM →</button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Meeting type edit dialog */}
      <Dialog open={!!editMT} onOpenChange={(v) => !v && setEditMT(null)}>
        <DialogContent className="max-w-md" data-testid="mt-dialog">
          <DialogHeader><DialogTitle>{editMT?.id ? 'Edit' : 'New'} meeting type</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={mtForm.name} onChange={(e) => setMtForm({ ...mtForm, name: e.target.value })} placeholder="Intro Call" data-testid="mt-name" /></div>
            <div><Label className="text-xs">Description</Label><Textarea rows={2} value={mtForm.description || ''} onChange={(e) => setMtForm({ ...mtForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Duration</Label>
                <Select value={String(mtForm.duration_minutes)} onValueChange={(v) => setMtForm({ ...mtForm, duration_minutes: Number(v) })}>
                  <SelectTrigger data-testid="mt-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[15, 30, 45, 60, 90].map((n) => <SelectItem key={n} value={String(n)}>{n} min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Select value={mtForm.location_type} onValueChange={(v) => setMtForm({ ...mtForm, location_type: v })}>
                  <SelectTrigger data-testid="mt-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video call</SelectItem>
                    <SelectItem value="phone">Phone call</SelectItem>
                    <SelectItem value="in_person">In person</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">
                {mtForm.location_type === 'video' && 'Video link (Zoom / Google Meet)'}
                {mtForm.location_type === 'phone' && 'Phone number to share'}
                {mtForm.location_type === 'in_person' && 'Address'}
              </Label>
              <Input value={mtForm.location_details || ''} onChange={(e) => setMtForm({ ...mtForm, location_details: e.target.value })} data-testid="mt-location-details" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={mtForm.is_active} onChange={(e) => setMtForm({ ...mtForm, is_active: e.target.checked })} />
              Active (visible to bookers)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMT(null)}>Cancel</Button>
            <Button onClick={saveMT} className="bg-magenta hover:bg-magenta/90" data-testid="save-mt">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
